/**
 * Commit an explicit Game Master HP adjustment through the BattleRoom server.
 *
 * Rule reference: a GM may override damage or healing, but the override must be
 * recorded and reconciled before the table advances. Local token mutation is
 * intentionally not accepted because the next authoritative turn projection
 * would replace it. date_of_change: 2026-09-01
 */
export const applyGmHitPointAdjustment = async ({
  sessionId,
  sendBattleRoomMutation,
  waitForAuthoritativeRevision,
  tokenId,
  tokenName,
  type,
  amount,
}) => {
  const numericAmount = Number(amount);
  const targetLabel = tokenName ? `${tokenName} (${tokenId || 'missing id'})` : (tokenId || 'Unknown combatant');

  if (!sessionId) {
    throw new Error(`${targetLabel} cannot be adjusted because no live BattleRoom session is connected.`);
  }
  if (typeof sendBattleRoomMutation !== 'function' || typeof waitForAuthoritativeRevision !== 'function') {
    throw new Error(`${targetLabel} cannot be adjusted because authoritative BattleRoom synchronization is unavailable.`);
  }
  if (!tokenId) {
    throw new Error('GM HP adjustment rejected: targetTokenId is required.');
  }
  if (!['damage', 'heal'].includes(type)) {
    throw new Error(`${targetLabel} cannot be adjusted: type must be "damage" or "heal".`);
  }
  if (!Number.isInteger(numericAmount) || numericAmount <= 0 || numericAmount > 100000) {
    throw new Error(`${targetLabel} cannot be adjusted: amount must be an integer from 1 to 100000.`);
  }

  const eventName = type === 'heal' ? 'healing:apply' : 'damage:apply';
  const mutationId = `gm-hp:${tokenId}:${type}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  const payload = type === 'heal'
    ? {
        sourceTokenId: tokenId,
        targetId: tokenId,
        amount: numericAmount,
        mutationId,
      }
    : {
        sourceTokenId: tokenId,
        targetId: tokenId,
        components: [{
          componentId: `gm-panel:${tokenId}:${Date.now()}`,
          damageType: 'untyped',
          rolledAmount: numericAmount,
        }],
        gmOverride: {
          enabled: true,
          reason: 'Explicit Game Master hit point adjustment',
        },
        mutationId,
      };

  let acknowledgement;
  try {
    acknowledgement = await sendBattleRoomMutation(eventName, payload);
  } catch (cause) {
    if (cause?.code !== 'REVISION_CONFLICT') throw cause;

    // A competing authoritative write can advance the room between the GM's
    // click and MongoDB's version-guarded commit. Reconcile that exact server
    // revision, then retry the same idempotent command once; never apply a
    // local fallback or generate a second mutation identity.
    // date_of_change: 2026-09-01
    await waitForAuthoritativeRevision(cause?.response?.revision);
    acknowledgement = await sendBattleRoomMutation(eventName, payload);
  }

  await waitForAuthoritativeRevision(acknowledgement?.revision);

  const resolution = acknowledgement?.result?.resolution || {};
  return {
    applied: Number(type === 'heal' ? resolution.applied : resolution.totalApplied) || numericAmount,
    hpBefore: Number.isFinite(Number(resolution.hpBefore)) ? Number(resolution.hpBefore) : null,
    hpAfter: Number.isFinite(Number(resolution.hpAfter)) ? Number(resolution.hpAfter) : null,
    revision: acknowledgement?.revision,
  };
};

export default applyGmHitPointAdjustment;
